/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import {
  ReservationsRecommendationPaymentOption as PaymentOption,
  ReservationsRecommendationTerm as Term,
  useCloudWasteSummaryCategorisedQuery,
  CloudWasteCategoryKey,
  useReservationsAndCommitmentsSavingsQuery,
  ReservationsAndCommitmentsLookbackPeriod,
  ReservationsRecommendationServiceType,
  useAzureReservationsSavingsQuery,
} from "~/generated/graphql";
import { useGetProviderAccountsCount } from "~/layout/MenuBar/submenus";
import { ROUTES } from "~/routing/routes";
import { isNotNil, sort } from "~/tools";
import { COLORS } from "~/views/home/components/TopSpendingApps/constants";
import { formatStringNumber } from "./utils";

export function useWasteSummary(riSavings: number, azureSavings: number) {
  const { data, loading, error } = useCloudWasteSummaryCategorisedQuery();
  const { data: summaryData } = useReservationsAndCommitmentsSavingsQuery({
    variables: {
      paymentOption: PaymentOption.PartialUpfront,
      term: Term.OneYear,
      lookbackPeriod: ReservationsAndCommitmentsLookbackPeriod.ThirtyDays,
    },
  });

  const { data: azureReservationsSavings } = useAzureReservationsSavingsQuery({
    variables: {
      paymentOption: PaymentOption.Monthly,
      term: Term.OneYear,
      lookbackPeriod: ReservationsAndCommitmentsLookbackPeriod.ThirtyDays,
      serviceType: ReservationsRecommendationServiceType.ShowAll,
    },
  });

  const { hasAWS, hasAzure } = useGetProviderAccountsCount();

  const wasteCategories =
    data?.cloudWasteSummaryCategorised?.categories?.filter((category) => {
      return showCategory(category?.category)
        ? parseInt(category?.cost ?? "0", 10) > 0
        : true;
    }) ?? [];

  const wasteSummary = sort(
    [
      ...wasteCategories.map((category) => {
        return {
          ...category,
          cost: Number(category?.cost ?? 0),
          estateRecords: category?.count ?? 0,
          costYearly: Number(category?.costYearly ?? 0),
          policies: category?.policies?.filter(isNotNil),
          uri: undefined,
          isVisible: true,
          isRICategory: false,
        };
      }),
      {
        category: "AWSRI",
        cost: riSavings ?? 0,
        costYearly: (riSavings ?? 0) * 12,
        estateRecords: 0,
        policies: undefined,
        uri: ROUTES.reservedInstances.index,
        isVisible: hasAWS,
      },
      {
        category: "AzureRI",
        cost: azureSavings ?? 0,
        costYearly: (azureSavings ?? 0) * 12,
        estateRecords: 0,
        policies: undefined,
        uri: ROUTES.reservedInstances.azureReservation,
        isVisible: hasAzure,
      },
    ],
    (a, b) => (b?.cost ?? 0) - (a?.cost ?? 0)
  );

  const barsValues = wasteSummary.map((policy, index) => ({
    color: COLORS[index],
    value: policy?.cost ?? 0,
  }));

  const azureReservations =
    formatStringNumber(azureReservationsSavings?.Compute.monthlySavings) +
    formatStringNumber(azureReservationsSavings?.Database.monthlySavings) +
    formatStringNumber(azureReservationsSavings?.Storage.monthlySavings) +
    formatStringNumber(azureReservationsSavings?.Analytics.monthlySavings);

  return {
    loading,
    error,
    summaryData,
    wasteSummary,
    barsValues,
    azureReservations,
  };
}

const CONDITIONAL_CATEGORIES = [
  CloudWasteCategoryKey.OverProvisioned,
  CloudWasteCategoryKey.Idle,
  CloudWasteCategoryKey.Turbonomic,
];

const showCategory = (category?: CloudWasteCategoryKey | null) => {
  return isNotNil(category) && CONDITIONAL_CATEGORIES.includes(category);
};
